import { useRouteMatch } from "react-router-dom";
import {
  ACCOUNT_EXTRA_DETAILS_PATH,
  ACCOUNT_EXTRA_DETAILS_TABS,
  ACCOUNT_EXTRA_DETAILS_TABS_TO_DISPLAY,
  DRAWER_TRADES_TABLE_FULL_WIDTH,
} from "common/constants";
import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import GeneralInfo from "features/portfolio/AccountDrawer/GeneralInfo";
import DrawerTitleLayout from "common/Drawers/DrawerTitleLayout";
import { accountExtraDetailsFilters } from "@shared/slices/filters.slice";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import DrawerTradesPage from "features/trades/common/DrawerTradesPage";
import { sharedLib } from "@shared/services/shared.service";
import AccountActions from "features/configuration/accounts/AccountActions";
import { getRouteWithoutPath } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { FilterType, LanguageNameSpaces } from "@shared/types";
import { useClientsData } from "@shared/utils/hooks/misc";
import {
  useBottomNavigator,
  useDrawerDelayedUnmount,
} from "utils/hooks/misc.tsx";
import { skipToken } from "@reduxjs/toolkit/query";
import Positions from "features/portfolio/Positions/Positions.tsx";
import {
  selectPortfolioByAccount,
  selectConsolidatedPortfolioByAccountData,
  selectPortfolioByAccountLoadingStates,
  selectPortfolioByAccountPrecision,
} from "@shared/slices/portfolio.slice.ts";
import Assets from "features/portfolio/Assets/Assets.tsx";
import PositionsProvider from "features/portfolio/Positions/PositionsProvider.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import { RootState } from "app/store";
import { ConsolidatedCoinData } from "@shared/types";

const AccountDrawer = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const match = useRouteMatch(ACCOUNT_EXTRA_DETAILS_PATH);
  const { historyPush } = useHistoryWithQuery();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();
  const { accountId: rawAccountId, tab: paramsTab } = (match?.params || {}) as {
    accountId: string;
    tab: string;
  };
  const defaultTab = paramsTab ?? ACCOUNT_EXTRA_DETAILS_TABS.GENERAL;
  const [tab, setTab] = useState(defaultTab);
  const accountId = parseInt(rawAccountId);
  const { id: drawerAccountId, isUnmounting } =
    useDrawerDelayedUnmount(accountId);
  const timeRange = useSelector(
    accountExtraDetailsFilters.selectors.selectTimeRangeFilter
  );
  const args = useMemo(
    () =>
      drawerAccountId
        ? {
            account_id: drawerAccountId,
            time_range: timeRange,
          }
        : skipToken,
    [drawerAccountId, timeRange]
  );
  const isDrawerOpen = !!accountId && !isUnmounting;
  const accountIdArgs = useMemo(
    () => (drawerAccountId ? { account_id: drawerAccountId } : skipToken),
    [drawerAccountId]
  );
  const options = { skip: !isDrawerOpen };
  const { getAccountById } = useClientsData();
  const accountData = getAccountById(drawerAccountId);
  const isPositionsTab = tab === ACCOUNT_EXTRA_DETAILS_TABS.POSITIONS;
  const shouldExpandDrawer =
    tab === ACCOUNT_EXTRA_DETAILS_TABS.TRADES ||
    tab === ACCOUNT_EXTRA_DETAILS_TABS.ASSETS ||
    isPositionsTab;

  useEffect(() => {
    if (isDrawerOpen && defaultTab !== tab) {
      setTab(defaultTab);
    }
  }, [isDrawerOpen]);

  const getPortfolioByAccountQuery =
    sharedLib.sharedApi.portfolio.useGetPortfolioByAccountQuery(
      accountIdArgs,
      options
    );

  const dataSelector = useMemo(() => {
    const selector = selectConsolidatedPortfolioByAccountData(accountIdArgs);
    if (!isNewLook) return selector;
    // New look: an account without balances comes back as `data: {}`, which
    // the shared selector can't consolidate (no exchange key); show the empty
    // state instead of crashing the Assets / Positions tabs.
    const portfolioSelector = selectPortfolioByAccount(accountIdArgs);
    return (state: RootState) => {
      const accountData = portfolioSelector(state)?.data;
      const exchanges = Object.keys(accountData || {}).filter(
        (key) => key !== "total" && key !== "available"
      );
      return accountData && !exchanges.length ? NO_COINS : selector(state);
    };
  }, [accountIdArgs, isNewLook]);
  const loadingSelector = useMemo(
    () => selectPortfolioByAccountLoadingStates(accountIdArgs),
    [accountIdArgs]
  );
  const precisionSelector = useMemo(
    () => selectPortfolioByAccountPrecision(accountIdArgs),
    [accountIdArgs]
  );

  const tabToComponents = {
    [ACCOUNT_EXTRA_DETAILS_TABS.GENERAL]: (
      <GeneralInfo args={args} accountData={accountData} />
    ),
    [ACCOUNT_EXTRA_DETAILS_TABS.ASSETS]: (
      <Assets
        dataSelector={dataSelector}
        loadingSelector={loadingSelector}
        precisionSelector={precisionSelector}
        getPortfolioDataQuery={getPortfolioByAccountQuery}
      />
    ),
    [ACCOUNT_EXTRA_DETAILS_TABS.POSITIONS]: (
      <PositionsProvider>
        <Positions
          dataSelector={dataSelector}
          loadingSelector={loadingSelector}
          precisionSelector={precisionSelector}
          getPortfolioDataQuery={getPortfolioByAccountQuery}
        />
      </PositionsProvider>
    ),
    [ACCOUNT_EXTRA_DETAILS_TABS.TRADES]: isDrawerOpen ? (
      <DrawerTradesPage
        baseGetTradesArgs={accountIdArgs}
        isDrawerOpen={isDrawerOpen}
        hideFilterTypes={TRADES_HIDE_FILTER_TYPES}
        tabKey={ACCOUNT_EXTRA_DETAILS_TABS.TRADES}
      />
    ) : null,
  };

  const closeDrawer = () => {
    historyPush(
      getRouteWithoutPath(location.pathname, ACCOUNT_EXTRA_DETAILS_PATH)
    );
  };

  const title = accountData ? (
    <DrawerTitleLayout
      title={
        <TextWithIcon
          text={accountData?.client_name}
          coinOrExchangeName={accountData?.exchange}
          iconType={ICON_TYPES.EXCHANGE}
          justify={"center"}
          style={{ width: "100%" }}
        />
      }
      titleRowActions={
        <AccountActions account={accountData} closeDrawer={closeDrawer} />
      }
    />
  ) : (
    ""
  );

  const contentStyle = useMemo(
    () =>
      isPositionsTab && !isBottomNavigator
        ? POSITIONS_INNER_CONTENT_WRAPPER_STYLE
        : { padding: isBottomNavigator ? 16 : "0 24px" },
    [isPositionsTab, isBottomNavigator]
  );

  return (
    <ExtraDetailsDrawer
      path={ACCOUNT_EXTRA_DETAILS_PATH}
      visible={isDrawerOpen}
      tabsToComponents={tabToComponents}
      onTabChange={setTab}
      title={title}
      tab={tab}
      minWidth={shouldExpandDrawer ? DRAWER_TRADES_TABLE_FULL_WIDTH : undefined}
      flatListTabs={FLAT_LIST_TABS}
      innerContentWrapperStyle={contentStyle}
      contentContainerStyle={
        isPositionsTab ? POSITIONS_CONTENT_CONTAINER_STYLE : undefined
      }
      getTabLabel={(tab) => t(ACCOUNT_EXTRA_DETAILS_TABS_TO_DISPLAY[tab])}
    />
  );
};

export default AccountDrawer;

const NO_COINS: ConsolidatedCoinData[] = [];
const TRADES_HIDE_FILTER_TYPES = [FilterType.EXCHANGES, FilterType.CLIENTS];
const FLAT_LIST_TABS = [
  ACCOUNT_EXTRA_DETAILS_TABS.TRADES,
  ACCOUNT_EXTRA_DETAILS_TABS.ASSETS,
  ACCOUNT_EXTRA_DETAILS_TABS.POSITIONS,
];
const POSITIONS_INNER_CONTENT_WRAPPER_STYLE = {
  padding: 0,
  overflow: "hidden",
};
const POSITIONS_CONTENT_CONTAINER_STYLE = { height: "100%" };
